"use client";

import classNames from "classnames";
// import { Stack } from "@fastlooptech/layout-components";
import { spaceUnit } from "@/helpers/utils.helper";
import { ImageType, T_KeyValue } from "@/types/app.type";
import { ColumnText } from "../ColumnText/ColumnText";
import { Display } from "@/client/foundations/Typography";
import { useResponsive } from "@/client/hooks/useResponsive";
import { renderToString } from "react-dom/server";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Stack } from "@/layouts/LayoutComponents";
import { variant } from "../../../components/Button/Button.css";
import { FC, ReactNode } from "react";
import ImageWrapper from "@/components/ImageWrapper";

export interface ColumnTextImageProps {
        id?: string;
        "data-test-id"?: string;
        /**
         * The ColumnTextImage className.
         * @type {string}
         */
        className?: string;
        /**
         * The image source of the ColumnTextImage.
         * @type ImageType
         * @required
         */
        image: ImageType;
        /**
         * The hero title of the ColumnTextImage.
         * @type string
         * @required
         */
        heroTitle: string;
        /**
         * Define the size of the hero title.
         * @type "s" | "m" | "l" | "xl"
         * @required
         */
        heroTitleSize: "s" | "m" | "l" | "xl";
        /**
         * The title of the ColumnTextImage.
         * @type string
         * @optional
         */
        title?: string;
        /**
         * The description of the ColumnTextImage.
         * @type string
         * @optional
         */
        subTitle?: string;
        /**
         * The first text block of the ColumnTextImage.
         * @type string
         * @optional
         */
        description?: any;
        /**
         * The cta of the ColumnTextImage.
         * @type ReactNode
         * @optional
         */
        cta?: ReactNode;
        /**
         * The variant ColumnTextImage.
         * @type string
         * @optional
         */
        layout?: string;
}

export function ColumnTextImage(props: ColumnTextImageProps) {
        const { isLg, isMd } = useResponsive();
        const {
                id,
                className,
                image,
                heroTitle,
                heroTitleSize,
                subTitle,
                cta,
                layout = "headingOverlap1",
        } = props;
        let title =
                subTitle && typeof subTitle === "object"
                        ? renderToString(documentToReactComponents(subTitle))
                        : subTitle;
        let description = props?.description
                ? renderToString(documentToReactComponents(props?.description))
                : "";
        const placeholderImage = {
                width: image?.width || 720,
                height: image?.height || 960,
                // src: image ? (image + '?fm=avif&fit=fill&f=center') : "https://placehold.co/720x960",
                src:
                        typeof image === "string"
                                ? image
                                : image?.src || "https://placehold.co/720x960",
        };

        const layouts: any = {
                headingOverlap1: isLg
                        ? "absolute top-3 !right-11 w-1/2"
                        : "w-full relative",
                headingOverlap2: isLg
                        ? "absolute top-7 !right-11 w-1/2"
                        : "w-full relative",
                textContentOverlap: "w-full h-full absolute px-2 bg-column-text-image",
                textContentLeft: isLg ? "!left-11 w-1/2" : "w-full relative",
                textContentRight: isLg ? "!right-11 w-1/2" : "w-full relative",
                textContentBottom: "w-full relative",
                textContentTop: "w-full relative",
        };

        const ContentJSX = ({
                children,
                className = "",
                style = {},
                column = !isLg,
        }: {
                children: ReactNode;
                className: string;
                style: T_KeyValue;
                column?: boolean;
        }) => (
                <Stack
                        gap={spaceUnit * 2}
                        data-test-id={props["data-test-id"]}
                        vAlignContent="bottom"
                        hAlignContent="center"
                        className={`bg-columnTextImage-main p-columnTextImage-main rounded-columnTextImage-main p-columnTextImage-main ${className}`}
                        style={style}
                        column={column}
                        id={id}
                >
                        {children}
                </Stack>
        );

        const ImageJSX = (className?: string) => (
                <ImageWrapper
                        data-test-id={props["data-test-id"] + "-image"}
                        alt={props["data-test-id"] + "-image"}
                        src={placeholderImage?.src}
                        placeholder="blur"
                        blurDataURL={placeholderImage?.src}
                        width={placeholderImage?.width}
                        height={placeholderImage?.height}
                        // priority
                        loading="lazy"
                        loader={({ src }) => src}
                        className={className}
                        // data-aos="fade-in"
                        dataAos="fade-in"
                />
        );

        const TextContentJSX = (className?: string, contentClassName?: string) => (
                <>
                        <Stack gap={spaceUnit * 6} column className={`${className}`}>
                                <Display
                                        size={heroTitleSize}
                                        animated={true}
                                        className={`text-interactive-columnTextImage-text-content-label p-columnTextImage-text-content-label`}
                                >
                                        {heroTitle}
                                </Display>
                                <ColumnText
                                        data-test-id={props["data-test-id"] + "-column-text"}
                                        title={title}
                                        subtitle={subTitle}
                                        textBlocks={[{ value: description || "", bold: false }]}
                                        cta={cta}
                                        className={`text-interactive-columnTextImage-text-content-text p-columnTextImage-text-content-text ${contentClassName}`}
                                />
                        </Stack>
                </>
        );

        return (
                <>
                        {/* <pre>{JSON.stringify(props, null, 2)}</pre> */}
                        {/* {placeholderImage?.src} */}
                        {(() => {
                                switch (layout) {
                                        case "textContentLeft":
                                                return (
                                                        <ContentJSX
                                                                className={classNames(
                                                                        "relative w-full max-w-[1440px] m-auto",
                                                                        className
                                                                )}
                                                                style={{ alignItems: "initial" }}
                                                        >
                                                                {TextContentJSX(
                                                                        classNames(
                                                                                "text-columnTextImage-text-content-main p-columnTextImage-text-content-main",
                                                                                layouts[layout]
                                                                        ),
                                                                        classNames(
                                                                                "w-full",
                                                                                !["headingOverlap1"].includes(layout) ? "" : "lg:pl-18"
                                                                        )
                                                                )}
                                                                {ImageJSX(
                                                                        classNames(
                                                                                "xl:w-2/4 object-cover object-center",
                                                                                isLg
                                                                                        ? "aspect-3/4 max-w-[695px] xl:max-w-[920px]"
                                                                                        : "pt-0 aspect-16/9 w-full"
                                                                        )
                                                                )}
                                                        </ContentJSX>
                                                );
                                        case "textContentTop":
                                                return (
                                                        <ContentJSX
                                                                className={classNames(
                                                                        "relative w-full max-w-[1440px] m-auto",
                                                                        className
                                                                )}
                                                                style={{ alignItems: "initial" }}
                                                                column={true}
                                                        >
                                                                {TextContentJSX(
                                                                        classNames(
                                                                                "text-columnTextImage-text-content-main p-columnTextImage-text-content-main",
                                                                                layouts[layout]
                                                                        ),
                                                                        classNames("w-full")
                                                                )}
                                                                {ImageJSX(
                                                                        classNames("object-cover object-center aspect-16/9 w-full")
                                                                )}
                                                        </ContentJSX>
                                                );
                                        case "textContentBottom":
                                                return (
                                                        <ContentJSX
                                                                className={classNames(
                                                                        "relative w-full max-w-[1440px] m-auto",
                                                                        className
                                                                )}
                                                                style={{ alignItems: "initial" }}
                                                                column={true}
                                                        >
                                                                {ImageJSX(
                                                                        classNames("object-cover object-center aspect-16/9 w-full")
                                                                )}
                                                                {TextContentJSX(
                                                                        classNames(
                                                                                "text-columnTextImage-text-content-main p-columnTextImage-text-content-main",
                                                                                layouts[layout]
                                                                        ),
                                                                        classNames("w-full")
                                                                )}
                                                        </ContentJSX>
                                                );
                                        case "textContentOverlap":
                                                return (
                                                        <ContentJSX
                                                                className={classNames(
                                                                        "relative w-full max-w-[1440px] m-auto overflow-hidden",
                                                                        className
                                                                )}
                                                                style={{ alignItems: "initial" }}
                                                                column={true}
                                                        >
                                                                {ImageJSX(
                                                                        classNames("object-cover object-center aspect-16/9 w-full")
                                                                )}
                                                                {TextContentJSX(
                                                                        classNames(
                                                                                "text-columnTextImage-text-content-main p-columnTextImage-text-content-main",
                                                                                layouts[layout]
                                                                        ),
                                                                        classNames("w-full")
                                                                )}
                                                        </ContentJSX>
                                                );
                                        default:
                                                return (
                                                        <ContentJSX
                                                                className={classNames(
                                                                        "relative w-full max-w-[1440px] m-auto",
                                                                        className
                                                                )}
                                                                style={
                                                                        !["headingOverlap1"].includes(layout)
                                                                                ? {
                                                                                        alignItems: "initial",
                                                                                }
                                                                                : {}
                                                                }
                                                                column={!isLg}
                                                        >
                                                                {ImageJSX(
                                                                        classNames(
                                                                                "xl:w-2/4 object-cover object-center",
                                                                                isLg
                                                                                        ? "aspect-3/4 max-w-[695px] xl:max-w-[920px]"
                                                                                        : "pt-0 aspect-16/9 w-full"
                                                                        )
                                                                )}
                                                                {TextContentJSX(
                                                                        layouts[layout],
                                                                        classNames(
                                                                                "w-full",
                                                                                !["headingOverlap1"].includes(layout) ? "" : "lg:pl-18"
                                                                        )
                                                                )}
                                                        </ContentJSX>
                                                );
                                }
                        })()}
                </>
        );
}
